import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareText } from 'lucide-react';
import { useCoachAudit } from '../../api/hooks';
import type { CoachAuditEvent } from '../../api/types';
import { Badge, EmptyState, ErrorPanel, PageHeader, PagedFooter, PanelCard, PillTabs, Spinner, Table, Td, Th } from '../../components/ui';
import { withTime } from '../../lib/dates';
import { TH_RULE } from '../../lib/tableRules';

const SCOPES = [
  { key: 'all', label: 'All' }, { key: 'student', label: 'Students' }, { key: 'section', label: 'Classes' },
  { key: 'grade', label: 'Grades' }, { key: 'exam', label: 'Exams' }, { key: 'school', label: 'School' }, { key: 'mine', label: 'My classes' },
] as const;
type ScopeKey = typeof SCOPES[number]['key'];

const SCOPE_WORD: Record<string, string> = {
  student: 'Student', section: 'Class', grade: 'Grade', exam: 'Exam', school: 'School', mine: 'My classes',
};

function scopeLink(e: CoachAuditEvent): string | null {
  if (e.scope_type === 'student' && e.scope_id) return `/students/profile/${e.scope_id}`;
  if (e.scope_type === 'section' && e.scope_id) return `/school/section/${e.scope_id}`;
  if (e.scope_type === 'exam' && e.scope_id) return `/exams/report/${e.scope_id}`;
  return null;
}

/** School admins: every question asked to the coach — who asked, about what, and what it looked up. */
export default function CoachActivityPage() {
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<ScopeKey>('all');
  const q = useCoachAudit({ page, ...(scope === 'all' ? {} : { scope_type: scope }) });
  const d = q.data;
  const pageCount = d ? Math.max(1, Math.ceil(d.count / d.page_size)) : 1;
  return (
    <div className="space-y-[18px]">
      <PageHeader title="Coach activity" trail={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Settings', to: '/settings/alerts' }]}
        subtitle="Every question staff asked the coach, newest first. Kept for accountability: the answers are not shown here." />
      <PanelCard>
        <div className="mb-3.5">
          <PillTabs<ScopeKey> label="About" value={scope} onChange={(k) => { setScope(k); setPage(1); }}
            options={SCOPES.map((s) => ({ key: s.key, label: s.label }))} />
        </div>
        {q.isLoading ? <div className="flex justify-center py-8"><Spinner size="md" /></div>
          : q.isError || !d ? <ErrorPanel />
          : d.events.length === 0 ? <EmptyState compact icon={<MessageSquareText size={18} aria-hidden />} title="No coach questions yet" />
          : (
            <>
              <Table minWidth={760}>
                <thead><tr>
                  <Th className={TH_RULE}>When</Th><Th className={TH_RULE}>Who</Th><Th className={TH_RULE}>About</Th>
                  <Th className={TH_RULE}>Question</Th><Th className={TH_RULE}>Looked up</Th>
                </tr></thead>
                <tbody>
                  {d.events.map((e) => {
                    const href = scopeLink(e);
                    return (
                      <tr key={e.id}>
                        <Td className="whitespace-nowrap font-mono text-[13px]">{withTime(e.created_at)}</Td>
                        <Td className="font-semibold">{e.user.name}</Td>
                        <Td className="text-[13px]">
                          <span className="block text-xs text-muted">{SCOPE_WORD[e.scope_type] ?? e.scope_type}</span>
                          {href ? <Link to={href} className="font-semibold text-ink hover:underline">{e.scope_label}</Link> : e.scope_label}
                        </Td>
                        <Td className="max-w-[340px] text-[13px]">
                          {e.question}
                          {e.status !== 'ok' && <Badge tone={e.status === 'error' ? 'danger' : 'neutral'} size="sm" className="ml-1.5">{e.status === 'error' ? 'Failed' : 'Stopped'}</Badge>}
                        </Td>
                        <Td className="text-xs text-muted">{e.tools_called.length ? e.tools_called.map((t) => t.replace(/_/g, ' ')).join(', ') : '—'}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <PagedFooter className="mt-3.5" label="Coach activity pagination" page={page} pageCount={pageCount} onPage={setPage}
                total={d.count} from={(page - 1) * d.page_size + 1} to={Math.min(page * d.page_size, d.count)} />
            </>
          )}
      </PanelCard>
    </div>
  );
}
